import React from 'react'
import styles from './MicrositeTestimonialsSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'
import MicrositeSlider from '../../../components/MicrositeSlider/MicrositeSlider'
import MicrositeImageUploader from '../../../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    title: '',
    subtitle: '',
    description: '',
    reviews: []
}

const genId = () => Date.now() + Math.floor(Math.random() * 1000)

const EMPTY_REVIEW = {
    id: null, // assigned on add, see addLabel handler below
    clientName: '',
    role: '',
    text: '',
    rating: 5,
    date: '',
    photoUrl: '',
    color: 'bg-white border border-black/5'
}

// Backfills any missing top-level key so older or partially-saved
// content still renders without crashing.
const withDefaults = (content) => ({
    title: content?.title ?? DEFAULT_CONTENT.title,
    subtitle: content?.subtitle ?? DEFAULT_CONTENT.subtitle,
    description: content?.description ?? DEFAULT_CONTENT.description,
    reviews: content?.reviews || []
})

/**
 * MicrositeTestimonialsSectionForm
 * Content editor for the landing page "Testimonials" section,
 * matching the real production content shape: title/subtitle/
 * description plus a repeatable list of reviews, each with a
 * numeric id, client name, role, review text, 1–5 rating, a
 * free-text date string, a photo URL, and a card background
 * color token (e.g. "bg-[#b0f2b6]" or a Tailwind utility string —
 * stored as free text since the live site interprets it directly).
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the matching
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeTestimonialsSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Testimonials</h2>
            <p className={styles.groupDesc}>Build trust by showing what your customers say about you.</p>

            <TextInput
                label="Title"
                value={safe.title}
                onChange={(v) => update('title', v)}
                placeholder="e.g. Client Stories"
                maxLength={80}
                showCharCount
            />
            <TextInput
                label="Subtitle"
                value={safe.subtitle}
                onChange={(v) => update('subtitle', v)}
                placeholder="e.g. Testimonials"
                maxLength={60}
                showCharCount
            />
            <TextInput
                label="Description"
                value={safe.description}
                onChange={(v) => update('description', v)}
                placeholder="e.g. Discover why our clients trust us for their beauty needs."
                maxLength={220}
                showCharCount
            />

            <label className={styles.rowsLabel}>Reviews</label>
            <MicrositeRepeatableRows
                items={safe.reviews}
                onChange={(v) => update('reviews', v)}
                emptyItem={{ ...EMPTY_REVIEW, id: genId() }}
                addLabel="Add review"
                renderSummary={(item) => item.clientName || 'New review'}
                renderItem={(item, idx, updateItem) => (
                    <div className={styles.reviewFields}>
                        <div className={styles.row2}>
                            <TextInput
                                label="Client name"
                                value={item.clientName}
                                onChange={(v) => updateItem('clientName', v)}
                                placeholder="e.g. Sarah Johnson"
                            />
                            <TextInput
                                label="Role"
                                value={item.role}
                                onChange={(v) => updateItem('role', v)}
                                placeholder="e.g. Regular Client"
                            />
                        </div>

                        <label className={styles.textareaLabel}>Review text</label>
                        <textarea
                            className={styles.textarea}
                            rows={3}
                            value={item.text || ''}
                            onChange={(e) => updateItem('text', e.target.value)}
                            placeholder="What did the client say about their experience?"
                        />

                        <MicrositeSlider
                            label="Rating"
                            value={item.rating}
                            onChange={(v) => updateItem('rating', v)}
                            min={1}
                            max={5}
                            step={1}
                        />

                        <div className={styles.row2}>
                            <TextInput
                                label="Date"
                                value={item.date}
                                onChange={(v) => updateItem('date', v)}
                                placeholder="e.g. August 29, 2024"
                            />
                            <TextInput
                                label="Card color"
                                value={item.color}
                                onChange={(v) => updateItem('color', v)}
                                placeholder="bg-[#b0f2b6] or bg-white border border-black/5"
                                helperText="Tailwind class string used for the card background"
                            />
                        </div>

                        <MicrositeImageUploader
                            label="Client photo"
                            value={item.photoUrl}
                            onChange={(v) => updateItem('photoUrl', v)}
                            placeholder="https://example.com/client.jpg"
                        />
                    </div>
                )}
            />
        </section>
    )
}

export default MicrositeTestimonialsSectionForm